'use client'

import { useAuth } from '@clerk/react'
import type { ReactNode } from 'react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-hot-toast'
import { trpc } from '@/lib/trpc'
import {
  DEFAULT_USER_SETTINGS,
  mergeSettings,
  type PartialUserSettings,
  type UserSettings,
} from '@/lib/user-settings'

interface UpdateSettingsOptions {
  /** Skip debounce and save immediately (use for toggles/discrete changes) */
  immediate?: boolean
}

interface UserSettingsContextValue {
  settings: UserSettings
  /** True once settings have been fetched from the server (or the user is not signed in). */
  settingsLoaded: boolean
  updateSettings: (
    partial: PartialUserSettings,
    options?: UpdateSettingsOptions,
  ) => void
}

const UserSettingsContext = createContext<UserSettingsContextValue | null>(null)

const DEBOUNCE_MS = 500

export function UserSettingsProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: authLoaded } = useAuth()
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS)
  // Tracks whether `settings` has actually been populated from the server
  // response. This is NOT the same as the query's isFetched — isFetched flips
  // true on the render data arrives, but setSettings(data.settings) fires in
  // the useEffect below, one render later. Consumers that gate on loading
  // must check this flag, otherwise they see DEFAULT_USER_SETTINGS during the
  // one-cycle window where the query resolved but state hasn't synced yet.
  const [settingsHydrated, setSettingsHydrated] = useState(false)
  const pendingUpdate = useRef<UserSettings | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const utils = trpc.useUtils()

  // Fetch settings via tRPC (only when signed in)
  const { data, isFetched } = trpc.user.getSettings.useQuery(undefined, {
    enabled: !!isSignedIn,
  })

  // Must wait for Clerk before trusting `isSignedIn`: it's `undefined` during
  // Clerk's initial load, and `!undefined === true` would otherwise flip
  // settingsLoaded true prematurely (no query runs, state never hydrates,
  // consumers see DEFAULT_USER_SETTINGS the whole time).
  const settingsLoaded = authLoaded && (!isSignedIn || settingsHydrated)

  // Sync fetched settings into local state (unless an optimistic update is
  // pending — in that case we skip the clobber but still mark hydrated).
  // Hydration is keyed off `isFetched`, which flips true on any terminal
  // query state (success or error). Without that, a network error would
  // leave settingsHydrated false forever and hang every consumer that gates
  // on settingsLoaded.
  useEffect(() => {
    if (!isSignedIn) {
      // Drop any pending debounced update. Sign-out doesn't unmount this
      // provider (Clerk just flips isSignedIn), so a leftover timer can
      // fire after a different user signs in on the same device, causing
      // the first user's settings to be written to the second user's
      // account via the mutation's current auth token.
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
      pendingUpdate.current = null
      setSettings(DEFAULT_USER_SETTINGS)
      setSettingsHydrated(false)
      return
    }

    if (data?.settings && !pendingUpdate.current) {
      setSettings(data.settings)
    }

    if (isFetched) {
      setSettingsHydrated(true)
    }
  }, [isSignedIn, data, isFetched])

  // Mutation for saving settings. Callers apply the change optimistically to
  // local state and (in several settings screens) show a "saved" toast right
  // away, so a silent failure here would leave the UI claiming success while
  // the change reverts on the next load. Observe the error: re-sync from the
  // server to undo the optimistic state and surface it to the user.
  const { mutate: saveSettings } = trpc.user.updateSettings.useMutation({
    onError: () => {
      utils.user.getSettings.invalidate()
      toast.error('Could not save your settings. Please try again.')
    },
  })

  // Flush pending settings to the API via tRPC mutation
  const flush = useCallback(
    (settingsToSave: UserSettings) => {
      saveSettings(settingsToSave)
    },
    [saveSettings],
  )

  const updateSettings = useCallback(
    (partial: PartialUserSettings, options?: UpdateSettingsOptions) => {
      // Compute merged settings inside setState (pure), then handle
      // side effects (debounce) outside the updater function
      let merged: UserSettings | null = null
      setSettings((prev) => {
        merged = mergeSettings(prev, partial)
        return merged
      })

      // Side effects: ref mutations and debounce timer
      if (merged) {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current)
        }

        if (options?.immediate) {
          // Save immediately (for toggles/discrete changes)
          flush(merged)
          pendingUpdate.current = null
        } else {
          pendingUpdate.current = merged
          debounceTimer.current = setTimeout(() => {
            if (pendingUpdate.current) {
              flush(pendingUpdate.current)
              pendingUpdate.current = null
            }
          }, DEBOUNCE_MS)
        }
      }
    },
    [flush],
  )

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
      // Flush any pending update immediately on unmount
      if (pendingUpdate.current) {
        flush(pendingUpdate.current)
      }
    }
  }, [flush])

  return (
    <UserSettingsContext
      value={{
        settings,
        settingsLoaded,
        updateSettings,
      }}
    >
      {children}
    </UserSettingsContext>
  )
}

export function useUserSettings() {
  const context = use(UserSettingsContext)
  if (!context) {
    throw new Error('useUserSettings must be used within UserSettingsProvider')
  }
  return context
}

/**
 * Optional version that returns defaults when outside provider.
 * Useful for components that may render during SSR or outside the provider tree.
 */
export function useUserSettingsOptional(): UserSettingsContextValue {
  const context = use(UserSettingsContext)
  if (!context) {
    return {
      settings: DEFAULT_USER_SETTINGS,
      settingsLoaded: false,
      updateSettings: () => {},
    }
  }
  return context
}
